import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useAuth } from '@/lib/auth';
import api from '@/lib/api';
import { toast } from 'sonner';
import {
  Package, Users, Search, AlertTriangle, Plus,
  CheckCircle2, Trash2, MessageSquare, FileSpreadsheet, Archive, Clock, ImagePlus, X
} from 'lucide-react';

const statusConfig = {
  'On Track': { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  'At Risk': { bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-500' },
  'Delayed': { bg: 'bg-red-50', text: 'text-red-700', dot: 'bg-red-500' },
  'Completed': { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
};

const emptyForm = {
  buyer_id: '', merchant_id: '', factory_id: '',
  style_name: '', quantity: '', delivery_date: '',
  pa_no: '', department: '', inspection_date: '',
  printing: 'no', embroidery: 'no', garment_wash: 'no', va_others: '',
  remarks: '', file_sent: 'no', file_sent_remarks: ''
};

const tabs = [
  { key: 'active', label: 'Active', icon: Package },
  { key: 'completed', label: 'Completed', icon: CheckCircle2 },
  { key: 'archived', label: 'Archived', icon: Archive },
];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({});
  const [buyers, setBuyers] = useState([]);
  const [merchants, setMerchants] = useState([]);
  const [factories, setFactories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterBuyer, setFilterBuyer] = useState('all');
  const [filterMerchant, setFilterMerchant] = useState('all');
  const [activeTab, setActiveTab] = useState('active');
  const [showAddOrder, setShowAddOrder] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ ...emptyForm });
  const [styleImage, setStyleImage] = useState(null);

  const fetchOrders = useCallback(async (lifecycle) => {
    setLoading(true);
    try {
      const res = await api.get(`/orders?lifecycle=${lifecycle}`);
      setOrders(res.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [statsRes, buyersRes, merchantsRes, factoriesRes] = await Promise.all([
          api.get('/dashboard/stats'),
          api.get('/users/buyers'),
          api.get('/users/merchants'),
          api.get('/factories'),
        ]);
        setStats(statsRes.data);
        setBuyers(buyersRes.data);
        setMerchants(merchantsRes.data);
        setFactories(factoriesRes.data);
      } catch (e) { console.error(e); }
    };
    loadData();
    fetchOrders(activeTab);
  }, [fetchOrders, activeTab]);

  useEffect(() => {
    fetchOrders(activeTab);
  }, [fetchOrders, activeTab]);

  const deleteOrder = async (orderId) => {
    if (!window.confirm('Delete this order permanently?')) return;
    try {
      await api.delete(`/orders/${orderId}`);
      setOrders(prev => prev.filter(o => o.id !== orderId));
      toast.success('Order deleted');
    } catch (e) { toast.error('Failed to delete order'); }
  };

  const changeLifecycle = async (orderId, newStatus) => {
    try {
      await api.put(`/orders/${orderId}/lifecycle`, { lifecycle_status: newStatus });
      setOrders(prev => prev.filter(o => o.id !== orderId));
      toast.success(`Order moved to ${newStatus}`);
      const statsRes = await api.get('/dashboard/stats');
      setStats(statsRes.data);
    } catch (e) { toast.error('Failed to update'); }
  };

  const handleAddOrder = async () => {
    if (!form.buyer_id || !form.style_name || !form.quantity) {
      toast.error('Buyer, Style Name, and Quantity are required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        buyer_id: form.buyer_id,
        style_name: form.style_name,
        quantity: parseInt(form.quantity, 10),
        delivery_date: form.delivery_date || null,
        pa_no: form.pa_no,
        department: form.department,
        inspection_date: form.inspection_date || null,
        remarks: form.remarks,
        file_sent: form.file_sent === 'yes',
        file_sent_remarks: form.file_sent_remarks,
        merchant_id: form.merchant_id && form.merchant_id !== 'none' ? form.merchant_id : null,
        factory_id: form.factory_id || null,
        value_additions: {
          printing: form.printing === 'yes',
          embroidery: form.embroidery === 'yes',
          garment_wash: form.garment_wash === 'yes',
          others: form.va_others || '',
        },
      };
      const res = await api.post('/orders', payload);
      // Upload style image if selected
      if (styleImage) {
        const imgData = new FormData();
        imgData.append('file', styleImage);
        await api.post(`/orders/${res.data.id}/image`, imgData, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      if (activeTab === 'active') setOrders(prev => [res.data, ...prev]);
      setShowAddOrder(false);
      setForm({ ...emptyForm });
      setStyleImage(null);
      toast.success('Order created');
      const statsRes = await api.get('/dashboard/stats');
      setStats(statsRes.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setSaving(false);
  };

  const filtered = orders.filter(o => {
    if (search && !o.style_name?.toLowerCase().includes(search.toLowerCase()) && !o.order_id?.toLowerCase().includes(search.toLowerCase()) && !o.buyer_name?.toLowerCase().includes(search.toLowerCase())) return false;
    if (filterStatus !== 'all' && o.status !== filterStatus) return false;
    if (filterBuyer !== 'all' && o.buyer_id !== filterBuyer) return false;
    if (filterMerchant !== 'all' && o.merchant_id !== filterMerchant) return false;
    return true;
  }).sort((a, b) => {
    const dateA = a.delivery_date ? new Date(a.delivery_date) : new Date('9999-12-31');
    const dateB = b.delivery_date ? new Date(b.delivery_date) : new Date('9999-12-31');
    return dateA - dateB;
  });

  const statCards = [
    { label: 'Active Orders', value: stats.total_orders || 0, icon: Package, color: 'text-[#1C1C1C]' },
    { label: 'On Track', value: stats.on_track || 0, icon: CheckCircle2, color: 'text-emerald-600' },
    { label: 'At Risk', value: stats.at_risk || 0, icon: AlertTriangle, color: 'text-amber-600' },
    { label: 'Delayed', value: stats.delayed || 0, icon: AlertTriangle, color: 'text-red-600' },
    { label: 'Users', value: stats.total_users || 0, icon: Users, color: 'text-[#6B7D5C]' },
    { label: 'New Leads', value: stats.new_leads || 0, icon: MessageSquare, color: 'text-[#C2A878]' },
  ];

  return (
    <DashboardLayout>
      <div data-testid="admin-dashboard" className="space-y-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-[#0A0A0A] tracking-tight">Dashboard</h1>
            <p className="text-sm text-[#52525B] mt-1">Manage orders, production & operations</p>
          </div>
          <div className="flex items-center gap-3">
            {currentUser?.role === 'super_admin' && (
              <Button onClick={() => { setForm({ ...emptyForm }); setShowAddOrder(true); }} variant="outline" className="rounded-md px-5 text-sm font-semibold border-black/[0.12]" data-testid="add-order-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Order
              </Button>
            )}
            {currentUser?.role === 'super_admin' && (
              <Button onClick={() => navigate('/dashboard/admin/upload')} className="bg-[#1C1C1C] hover:bg-[#0A0A0A] text-white rounded-md px-5 text-sm font-semibold btn-premium" data-testid="upload-excel-btn">
                <FileSpreadsheet className="w-4 h-4 mr-2" /> Upload Excel
              </Button>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {statCards.map((s) => (
            <div key={s.label} className="bg-white rounded-md border border-black/[0.06] p-4">
              <div className="flex items-center justify-between">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-[#A1A1AA]">{s.label}</p>
                <s.icon className={`w-3.5 h-3.5 ${s.color}`} />
              </div>
              <p className="font-heading text-2xl font-semibold text-[#0A0A0A] mt-1">{s.value}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 bg-white rounded-md border border-black/[0.06] p-1 w-fit">
          {tabs.map((tab) => {
            const count = tab.key === 'active' ? stats.total_orders : tab.key === 'completed' ? stats.completed_orders : stats.archived_orders;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === tab.key ? 'bg-[#1C1C1C] text-white' : 'text-[#52525B] hover:bg-[#F5F5F5]'}`}
                data-testid={`tab-${tab.key}`}
              >
                <tab.icon className="w-3.5 h-3.5" />
                {tab.label}
                {count !== undefined && count > 0 && (
                  <span className={`text-[11px] font-semibold px-1.5 py-0.5 rounded-full ${activeTab === tab.key ? 'bg-white/20 text-white' : 'bg-black/[0.06] text-[#52525B]'}`}>{count}</span>
                )}
              </button>
            );
          })}
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A1A1AA]" />
            <Input placeholder="Search by PO, style, or buyer..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 h-10 rounded-md border-black/[0.08]" data-testid="admin-search-input" />
          </div>
          <Select value={filterBuyer} onValueChange={setFilterBuyer}>
            <SelectTrigger className="w-[180px] h-10 rounded-md border-black/[0.08]" data-testid="filter-buyer">
              <SelectValue placeholder="All Buyers" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Buyers</SelectItem>
              {buyers.map(b => <SelectItem key={b.id} value={b.id}>{b.name} — {b.company || ''}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterMerchant} onValueChange={setFilterMerchant}>
            <SelectTrigger className="w-[180px] h-10 rounded-md border-black/[0.08]" data-testid="filter-merchant">
              <SelectValue placeholder="All Merchants" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Merchants</SelectItem>
              {merchants.map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[160px] h-10 rounded-md border-black/[0.08]" data-testid="filter-status">
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="On Track">On Track</SelectItem>
              <SelectItem value="At Risk">At Risk</SelectItem>
              <SelectItem value="Delayed">Delayed</SelectItem>
              <SelectItem value="Completed">Completed</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Orders Table */}
        <div className="bg-white rounded-md border border-black/[0.06] overflow-hidden">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="w-6 h-6 border-2 border-[#1C1C1C] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-b border-black/[0.04] hover:bg-transparent">
                  <TableHead className="text-[11px] font-semibold uppercase tracking-wide text-[#A1A1AA] h-11">PA</TableHead>
                  <TableHead className="text-[11px] font-semibold uppercase tracking-wide text-[#A1A1AA] h-11">Style</TableHead>
                  <TableHead className="text-[11px] font-semibold uppercase tracking-wide text-[#A1A1AA] h-11 hidden md:table-cell">Buyer</TableHead>
                  <TableHead className="text-[11px] font-semibold uppercase tracking-wide text-[#A1A1AA] h-11 hidden lg:table-cell">Merchant</TableHead>
                  <TableHead className="text-[11px] font-semibold uppercase tracking-wide text-[#A1A1AA] h-11 hidden lg:table-cell">Factory</TableHead>
                  <TableHead className="text-[11px] font-semibold uppercase tracking-wide text-[#A1A1AA] h-11 hidden lg:table-cell">Qty</TableHead>
                  <TableHead className="text-[11px] font-semibold uppercase tracking-wide text-[#A1A1AA] h-11 hidden lg:table-cell">Delivery</TableHead>
                  <TableHead className="text-[11px] font-semibold uppercase tracking-wide text-[#A1A1AA] h-11">Progress</TableHead>
                  <TableHead className="text-[11px] font-semibold uppercase tracking-wide text-[#A1A1AA] h-11">Status</TableHead>
                  <TableHead className="text-[11px] font-semibold uppercase tracking-wide text-[#A1A1AA] h-11 w-24">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((order) => {
                  const sc = statusConfig[order.status] || statusConfig['On Track'];
                  return (
                    <TableRow key={order.id} className="table-row-hover cursor-pointer border-b border-black/[0.03]" onClick={() => navigate(`/dashboard/admin/orders/${order.id}`)} data-testid={`admin-order-row-${order.order_id}`}>
                      <TableCell className="text-sm font-semibold text-[#0A0A0A]">{order.order_id}</TableCell>
                      <TableCell>
                        <p className="text-sm font-medium text-[#0A0A0A] truncate max-w-[200px]">{order.style_name}</p>
                        {order.department && <p className="text-[11px] text-[#A1A1AA]">{order.department}</p>}
                      </TableCell>
                      <TableCell className="text-sm text-[#52525B] hidden md:table-cell">{order.buyer_name}</TableCell>
                      <TableCell className="text-sm text-[#52525B] hidden lg:table-cell">{order.merchant_name || '-'}</TableCell>
                      <TableCell className="text-sm text-[#52525B] hidden lg:table-cell">{order.factory_name || '-'}</TableCell>
                      <TableCell className="text-sm text-[#52525B] hidden lg:table-cell">{order.quantity?.toLocaleString()}</TableCell>
                      <TableCell className="text-sm text-[#52525B] hidden lg:table-cell">
                        {order.delivery_date ? new Date(order.delivery_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' }) : '-'}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 min-w-[100px]">
                          <Progress value={order.progress} className={`h-1.5 bg-black/[0.06] flex-1 ${order.status === 'Delayed' ? '[&>div]:bg-red-500' : order.status === 'At Risk' ? '[&>div]:bg-amber-500' : '[&>div]:bg-emerald-500'}`} />
                          <span className="text-xs font-semibold text-[#0A0A0A] w-8 text-right">{order.progress}%</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className={`status-badge ${sc.bg} ${sc.text}`}>
                          <div className={`w-1.5 h-1.5 rounded-full ${sc.dot}`} />
                          {order.status}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                          {activeTab === 'active' && (
                            <button onClick={() => changeLifecycle(order.id, 'completed')} className="p-1.5 rounded-md hover:bg-emerald-50 text-[#A1A1AA] hover:text-emerald-600 transition-colors" title="Mark Completed" data-testid={`complete-${order.order_id}`}>
                              <CheckCircle2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                          {activeTab === 'completed' && (
                            <>
                              <button onClick={() => changeLifecycle(order.id, 'active')} className="p-1.5 rounded-md hover:bg-blue-50 text-[#A1A1AA] hover:text-blue-600 transition-colors" title="Reactivate" data-testid={`reactivate-${order.order_id}`}>
                                <Package className="w-3.5 h-3.5" />
                              </button>
                              <button onClick={() => changeLifecycle(order.id, 'archived')} className="p-1.5 rounded-md hover:bg-amber-50 text-[#A1A1AA] hover:text-amber-600 transition-colors" title="Archive Now" data-testid={`archive-${order.order_id}`}>
                                <Archive className="w-3.5 h-3.5" />
                              </button>
                            </>
                          )}
                          {activeTab === 'archived' && (
                            <button onClick={() => changeLifecycle(order.id, 'active')} className="p-1.5 rounded-md hover:bg-blue-50 text-[#A1A1AA] hover:text-blue-600 transition-colors" title="Reactivate" data-testid={`reactivate-${order.order_id}`}>
                              <Package className="w-3.5 h-3.5" />
                            </button>
                          )}
                          <button onClick={() => deleteOrder(order.id)} className="p-1.5 rounded-md hover:bg-red-50 text-[#A1A1AA] hover:text-red-500 transition-colors" title="Delete" data-testid={`delete-order-${order.order_id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {filtered.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center py-16 text-sm text-[#A1A1AA]">
                      {activeTab === 'active' ? 'No active orders. Upload an Excel file or add an order manually.'
                        : activeTab === 'completed' ? 'No completed orders.'
                        : 'No archived orders.'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </div>
      </div>

      {/* Add Order Dialog */}
      <Dialog open={showAddOrder} onOpenChange={setShowAddOrder}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="add-order-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-semibold">Add New Order</DialogTitle>
            <DialogDescription>Create a single order manually.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            {/* Style Image Upload */}
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wide text-[#52525B]">Style Image</Label>
              {styleImage ? (
                <div className="relative w-full h-32 rounded-md border border-black/[0.08] overflow-hidden bg-[#FAFAF8]">
                  <img src={URL.createObjectURL(styleImage)} alt="Style preview" className="w-full h-full object-contain" />
                  <button
                    type="button"
                    onClick={() => setStyleImage(null)}
                    className="absolute top-2 right-2 p-1 rounded-full bg-black/60 text-white hover:bg-black/80 transition-colors"
                    data-testid="remove-style-image-btn"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center w-full h-24 rounded-md border-2 border-dashed border-black/[0.1] bg-[#FAFAF8] cursor-pointer hover:border-black/[0.2] transition-colors" data-testid="style-image-upload-area">
                  <ImagePlus className="w-5 h-5 text-[#A1A1AA] mb-1" />
                  <span className="text-xs text-[#A1A1AA]">Click to upload style image</span>
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => { if (e.target.files[0]) setStyleImage(e.target.files[0]); }} data-testid="style-image-input" />
                </label>
              )}
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wide text-[#52525B]">Buyer *</Label>
              <Select value={form.buyer_id} onValueChange={(v) => setForm(p => ({ ...p, buyer_id: v }))}>
                <SelectTrigger className="h-10 rounded-md" data-testid="order-buyer-select"><SelectValue placeholder="Select buyer" /></SelectTrigger>
                <SelectContent>{buyers.map(b => (<SelectItem key={b.id} value={b.id}>{b.name} — {b.company || 'N/A'}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wide text-[#52525B]">Merchant</Label>
                <Select value={form.merchant_id} onValueChange={(v) => setForm(p => ({ ...p, merchant_id: v }))}>
                  <SelectTrigger className="h-10 rounded-md" data-testid="order-merchant-select"><SelectValue placeholder="Select merchant" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Merchant</SelectItem>
                    {merchants.map(m => (<SelectItem key={m.id} value={m.id}>{m.name} ({m.email})</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wide text-[#52525B]">Factory *</Label>
                <Select value={form.factory_id} onValueChange={(v) => setForm(p => ({ ...p, factory_id: v }))}>
                  <SelectTrigger className="h-10 rounded-md" data-testid="order-factory-select"><SelectValue placeholder="Select factory" /></SelectTrigger>
                  <SelectContent>
                    {factories.map(f => (<SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wide text-[#52525B]">PO / Order ID</Label>
                <Input value={form.pa_no} onChange={(e) => setForm(p => ({ ...p, pa_no: e.target.value }))} placeholder="e.g., 5200658925" className="h-10 rounded-md" data-testid="order-po-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wide text-[#52525B]">Style Name *</Label>
                <Input value={form.style_name} onChange={(e) => setForm(p => ({ ...p, style_name: e.target.value }))} placeholder="e.g., S25D02 FA SW1" className="h-10 rounded-md" data-testid="order-style-input" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wide text-[#52525B]">Quantity *</Label>
                <Input type="number" value={form.quantity} onChange={(e) => setForm(p => ({ ...p, quantity: e.target.value }))} placeholder="e.g., 1500" className="h-10 rounded-md" data-testid="order-qty-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wide text-[#52525B]">Department</Label>
                <Input value={form.department} onChange={(e) => setForm(p => ({ ...p, department: e.target.value }))} placeholder="e.g., 12WODRMIN" className="h-10 rounded-md" data-testid="order-dept-input" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wide text-[#52525B]">File Sent</Label>
                <Select value={form.file_sent} onValueChange={(v) => setForm(p => ({ ...p, file_sent: v }))}>
                  <SelectTrigger className="h-10 rounded-md" data-testid="order-file-sent-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="no">No</SelectItem>
                    <SelectItem value="yes">Yes</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wide text-[#52525B]">File Sent Remarks</Label>
                <Input value={form.file_sent_remarks} onChange={(e) => setForm(p => ({ ...p, file_sent_remarks: e.target.value }))} placeholder="Notes about file..." className="h-10 rounded-md" data-testid="order-file-sent-remarks" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wide text-[#52525B]">Remarks</Label>
              <Input value={form.remarks} onChange={(e) => setForm(p => ({ ...p, remarks: e.target.value }))} placeholder="Any notes..." className="h-10 rounded-md" data-testid="order-remarks-input" />
            </div>
            {/* Value Additions */}
            <div className="space-y-3 pt-2">
              <Label className="text-xs font-semibold uppercase tracking-wide text-[#52525B]">Value Additions</Label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="space-y-1.5">
                  <p className="text-xs text-[#71717A] font-medium">Printing</p>
                  <Select value={form.printing} onValueChange={(v) => setForm(p => ({ ...p, printing: v }))}>
                    <SelectTrigger className="h-9 rounded-md text-sm" data-testid="order-printing-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="no">No</SelectItem>
                      <SelectItem value="yes">Yes</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <p className="text-xs text-[#71717A] font-medium">Embroidery</p>
                  <Select value={form.embroidery} onValueChange={(v) => setForm(p => ({ ...p, embroidery: v }))}>
                    <SelectTrigger className="h-9 rounded-md text-sm" data-testid="order-embroidery-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="no">No</SelectItem>
                      <SelectItem value="yes">Yes</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <p className="text-xs text-[#71717A] font-medium">Garment Wash</p>
                  <Select value={form.garment_wash} onValueChange={(v) => setForm(p => ({ ...p, garment_wash: v }))}>
                    <SelectTrigger className="h-9 rounded-md text-sm" data-testid="order-wash-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="no">No</SelectItem>
                      <SelectItem value="yes">Yes</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1.5">
                <p className="text-xs text-[#71717A] font-medium">Others</p>
                <Input value={form.va_others} onChange={(e) => setForm(p => ({ ...p, va_others: e.target.value }))} placeholder="Any other value additions..." className="h-9 rounded-md text-sm" data-testid="order-va-others-input" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wide text-[#52525B]">Delivery Date</Label>
                <Input type="date" value={form.delivery_date?.split('T')[0] || ''} onChange={(e) => setForm(p => ({ ...p, delivery_date: e.target.value ? new Date(e.target.value).toISOString() : '' }))} className="h-10 rounded-md" data-testid="order-delivery-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wide text-[#52525B]">Inspection Date</Label>
                <Input type="date" value={form.inspection_date?.split('T')[0] || ''} onChange={(e) => setForm(p => ({ ...p, inspection_date: e.target.value ? new Date(e.target.value).toISOString() : '' }))} className="h-10 rounded-md" data-testid="order-insp-input" />
              </div>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowAddOrder(false)} className="rounded-md" data-testid="cancel-order-btn">Cancel</Button>
            <Button onClick={handleAddOrder} disabled={saving} className="bg-[#1C1C1C] hover:bg-[#0A0A0A] text-white rounded-md btn-premium" data-testid="save-order-btn">
              {saving ? 'Creating...' : 'Create Order'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
